import React, { useEffect, useState, useMemo } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, DollarSign, Settings as SettingsIcon, ExternalLink, LogOut,
  Search, Loader2, CheckCircle2, RotateCcw, Mail, Download, ChevronDown,
  ChevronRight, FileText, ScrollText, Award, UserPlus, Edit,
  Shield, Trash2, KeyRound, BarChart3, Globe, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { getAdminToken, getAdminUser, clearAdminAuth } from "@/lib/adminAuth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const TABS = [
  { id: "enrollments", label: "Enrollments", Icon: Users },
  { id: "transcripts", label: "Transcript Requests", Icon: ScrollText },
  { id: "instant_records", label: "Instant Records", Icon: Award },
  { id: "stats", label: "Web Stats", Icon: BarChart3 },
  { id: "pricing", label: "Pricing", Icon: DollarSign },
  { id: "payment", label: "Payment Instructions", Icon: FileText },
  { id: "site_content", label: "Site Content", Icon: FileText },
  { id: "policies", label: "Policies", Icon: ScrollText },
  { id: "email_diagnostics", label: "Email Diagnostics", Icon: Mail },
  { id: "users", label: "Admin Users", Icon: Shield, superOnly: true },
];

export default function AdminDashboardPage() {
  const nav = useNavigate();
  const token = getAdminToken();
  const user = getAdminUser();
  const [tab, setTab] = useState("enrollments");

  if (!token) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    clearAdminAuth();
    toast.success("Signed out.");
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-grey-light flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-purple-dark text-white flex flex-col shrink-0">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <img src="/zca-logo.png" alt="ZCA" className="h-9 w-9 bg-white rounded-sm p-0.5" />
            <div className="h-serif text-lg text-gold-light leading-tight">
              Zion Admin
            </div>
          </div>
          <div className="text-xs text-white/60 truncate">{user?.full_name || user?.email}</div>
          <div className="text-[10px] tracking-widest uppercase text-gold mt-1">
            {user?.role?.replace("_", " ")}
          </div>
        </div>
        <nav className="flex-1 py-4">
          {TABS.filter((t) => !t.superOnly || user?.role === "super_admin").map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                tab === t.id
                  ? "bg-purple text-white border-l-4 border-gold"
                  : "text-white/75 hover:bg-white/5 border-l-4 border-transparent"
              }`}
            >
              <t.Icon size={16} />
              {t.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          <Link
            to="/"
            className="flex items-center gap-2 text-xs text-white/70 hover:text-gold-light px-2 py-2"
            data-testid="view-website-link"
          >
            <ExternalLink size={14} /> View website
          </Link>
          <button
            onClick={logout}
            className="flex items-center gap-2 text-xs text-white/70 hover:text-red-300 px-2 py-2 w-full"
            data-testid="logout-btn"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-auto">
        {tab === "enrollments" && <EnrollmentsTab token={token} />}
        {tab === "transcripts" && <TranscriptQueueTab token={token} />}
        {tab === "instant_records" && <InstantRecordsQueueTab token={token} />}
        {tab === "stats" && <WebStatsTab token={token} />}
        {tab === "pricing" && <PricingTab token={token} />}
        {tab === "payment" && <PaymentInstructionsTab token={token} />}
        {tab === "site_content" && <SiteContentTab token={token} />}
        {tab === "policies" && <PoliciesTab token={token} />}
        {tab === "email_diagnostics" && <EmailDiagnosticsTab token={token} />}
        {tab === "users" && <AdminUsersTab token={token} user={user} />}
      </main>
    </div>
  );
}

/* ------------ Enrollments Tab ------------ */
function EnrollmentsTab({ token }) {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [counts, setCounts] = useState({ active_count: 0, archived_count: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [archiveScope, setArchiveScope] = useState("false"); // "false" | "true" | "all"
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [enrRes, statRes] = await Promise.all([
        api.get("/admin/enrollments", { params: { token, archived: archiveScope } }),
        api.get("/admin/stats", { params: { token } }),
      ]);
      setRows(enrRes.data.enrollments || []);
      setCounts({
        active_count: enrRes.data.active_count || 0,
        archived_count: enrRes.data.archived_count || 0,
      });
      setStats(statRes.data);
    } catch (e) {
      toast.error("Failed to load enrollments.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [archiveScope]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.payment_status !== statusFilter) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return (
        r.enrollment_id?.toLowerCase().includes(s) ||
        r.parent_first_name?.toLowerCase().includes(s) ||
        r.parent_last_name?.toLowerCase().includes(s) ||
        r.email?.toLowerCase().includes(s) ||
        (r.students || []).some((st) => st.full_name?.toLowerCase().includes(s))
      );
    });
  }, [rows, q, statusFilter]);

  const setBusyFor = (id, val) => setBusy((b) => ({ ...b, [id]: val }));

  const markPaid = async (id) => {
    setBusyFor(id, "paid");
    try {
      const { data } = await api.post(`/admin/enrollments/${id}/mark-paid`, null, { params: { token } });
      toast.success(`Marked paid · ${data.documents_generated} PDFs emailed`);
      await load();
    } catch { toast.error("Failed to mark paid."); }
    finally { setBusyFor(id, null); }
  };
  const resendDocs = async (id) => {
    setBusyFor(id, "resend");
    try {
      const res = await api.post(`/admin/enrollments/${id}/resend-docs`, null, { params: { token } });
      if (res.data?.email_sent) {
        toast.success("Documents re-emailed to the parent.");
      } else {
        toast.warning("The request went through, but delivery wasn't confirmed. If the family doesn't receive it shortly, contact support.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resend documents.");
    }
    finally { setBusyFor(id, null); }
  };
  const resendEnrollmentEmail = async (id) => {
    setBusyFor(id, "resendmail");
    try {
      const res = await api.post(`/admin/enrollments/${id}/resend-enrollment-email`, null, { params: { token } });
      if (res.data?.email_sent) {
        toast.success("Enrollment-received email resent.");
      } else {
        toast.warning("The request went through, but delivery wasn't confirmed. If the family doesn't receive it shortly, contact support.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resend enrollment email.");
    }
    finally { setBusyFor(id, null); }
  };
  const remind = async (id) => {
    setBusyFor(id, "remind");
    try {
      await api.post(`/admin/enrollments/${id}/remind-unpaid`, null, { params: { token } });
      toast.success("Reminder email sent.");
    } catch { toast.error("Failed to send reminder."); }
    finally { setBusyFor(id, null); }
  };
  const removeEnrollment = async (id, familyLabel) => {
    if (!window.confirm(`Archive enrollment ${id} (${familyLabel})?\n\nArchived enrollments are hidden from the main list but can be restored from the Archived filter. Nothing is permanently deleted yet.`)) return;
    setBusyFor(id, "delete");
    try {
      await api.delete(`/admin/enrollments/${id}`, { params: { token } });
      toast.success(`Archived ${id}. View it under the Archived filter.`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Archive failed."); }
    finally { setBusyFor(id, null); }
  };
  const restoreEnrollment = async (id) => {
    setBusyFor(id, "restore");
    try {
      await api.post(`/admin/enrollments/${id}/restore`, null, { params: { token } });
      toast.success(`Restored ${id}.`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Restore failed."); }
    finally { setBusyFor(id, null); }
  };

  const [addOpen, setAddOpen] = useState(false);
  const [addResult, setAddResult] = useState(null);
  const [editing, setEditing] = useState(null); // enrollment row being edited
  const permanentDelete = async (id, familyLabel) => {
    if (!window.confirm(`Permanently delete enrollment ${id} (${familyLabel})?\n\nThis is IRREVERSIBLE. All data for this family will be erased from the database.`)) return;
    setBusyFor(id, "purge");
    try {
      await api.delete(`/admin/enrollments/${id}/permanent`, { params: { token } });
      toast.success(`${id} permanently deleted.`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Permanent delete failed."); }
    finally { setBusyFor(id, null); }
  };
  const exportCSV = () => {
    const rows2 = [["Enrollment ID","Parent","Email","Phone","Students","Payment","Method","School Year","Amount Due","Created"]];
    filtered.forEach((r) => rows2.push([
      r.enrollment_id, `${r.parent_first_name} ${r.parent_last_name}`, r.email, r.phone,
      (r.students||[]).map(s=>s.full_name).join(" | "),
      r.payment_status, r.payment_method, r.school_year,
      r.amount_due, r.created_at,
    ]));
    const csv = rows2.map(r => r.map(v => `"${String(v ?? "").replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `zca_enrollments_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8 max-w-full">
      <Header title="Enrollments" subtitle="Every family, every student, every payment." />
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat label="Total families" value={stats.total_families} />
          <Stat label="Paid" value={stats.paid_families} accent="emerald" />
          <Stat label="Unpaid" value={stats.unpaid_families} accent="amber" />
          <Stat label="Revenue collected" value={`$${(stats.revenue_collected || 0).toLocaleString()}`} accent="gold" />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
          <Input placeholder="Search by name, email, or ID..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 border-grey bg-white" data-testid="enrollments-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 border-grey bg-white" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
          </SelectContent>
        </Select>
        <Select value={archiveScope} onValueChange={setArchiveScope}>
          <SelectTrigger className="w-44 border-grey bg-white" data-testid="archive-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="false">Active ({counts.active_count})</SelectItem>
            <SelectItem value="true">Archived ({counts.archived_count})</SelectItem>
            <SelectItem value="all">All (incl. archived)</SelectItem>
          </SelectContent>
        </Select>
        <button onClick={load} className="border border-purple text-purple hover:bg-white px-4 py-2 rounded-sm text-sm inline-flex items-center gap-2 font-bold" data-testid="refresh-btn">
          <RotateCcw size={14} /> Refresh
        </button>
        <button onClick={exportCSV} className="bg-purple text-white hover:bg-purple-dark px-4 py-2 rounded-sm text-sm inline-flex items-center gap-2 font-bold" data-testid="export-csv-btn">
          <Download size={14} /> Export CSV
        </button>
        <button
          onClick={() => setAddOpen(true)}
          className="bg-gold text-ink hover:bg-gold-light px-4 py-2 rounded-sm text-sm inline-flex items-center gap-2 font-bold"
          data-testid="add-family-btn"
        >
          <UserPlus size={14} /> Add Family
        </button>
      </div>
      {addOpen && (
        <AddFamilyModal
          token={token}
          onClose={() => setAddOpen(false)}
          onCreated={(r) => { setAddResult(r); load(); }}
        />
      )}
      {addResult && (
        <AddFamilyResultModal result={addResult} onClose={() => setAddResult(null)} />
      )}
      {editing && (
        <EditEnrollmentModal
          token={token}
          enrollment={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {loading ? (
        <div className="flex items-center gap-3 text-purple py-10"><Loader2 className="animate-spin" size={18} /> Loading...</div>
      ) : (
        <div className="bg-white border border-grey">
          <table className="w-full text-sm" data-testid="enrollments-table">
            <thead className="bg-grey-light text-xs tracking-widest uppercase text-purple-dark">
              <tr>
                <th className="text-left p-3 w-8"></th>
                <th className="text-left p-3">Enrollment ID</th>
                <th className="text-left p-3">Family</th>
                <th className="text-left p-3">Students</th>
                <th className="text-left p-3">Method</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Year</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const isPaid = r.payment_status === "paid";
                const isExp = expanded[r.enrollment_id];
                return (
                  <React.Fragment key={r.enrollment_id}>
                    <tr className="border-t border-grey hover:bg-grey-light/50" data-testid={`enrollment-row-${r.enrollment_id}`}>
                      <td className="p-3">
                        <button onClick={() => setExpanded((e) => ({ ...e, [r.enrollment_id]: !e[r.enrollment_id] }))} className="text-purple">
                          {isExp ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      </td>
                      <td className="p-3 font-mono text-xs text-purple-dark">{r.enrollment_id}</td>
                      <td className="p-3">
                        <div className="font-bold text-ink">{r.parent_first_name} {r.parent_last_name}</div>
                        <div className="text-xs text-ink/60">{r.email}</div>
                      </td>
                      <td className="p-3 text-xs">{(r.students || []).length}</td>
                      <td className="p-3 text-xs">{r.payment_method === "cash_check_now" ? "Cash/Check" : "Plan"}</td>
                      <td className="p-3">
                        <span className={`text-xs font-bold px-2 py-1 rounded-sm ${isPaid ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                          {r.payment_status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="p-3 text-xs">{r.school_year}</td>
                      <td className="p-3 text-right space-x-1">
                        {!isPaid && (
                          <>
                            <button onClick={() => markPaid(r.enrollment_id)} disabled={!!busy[r.enrollment_id]} className="bg-gold text-ink hover:bg-gold-light font-bold px-3 py-1.5 rounded-sm text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid={`mark-paid-${r.enrollment_id}`}>
                              {busy[r.enrollment_id] === "paid" ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Mark Paid
                            </button>
                            <button onClick={() => remind(r.enrollment_id)} disabled={!!busy[r.enrollment_id]} className="border border-purple text-purple hover:bg-white px-3 py-1.5 rounded-sm text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid={`remind-${r.enrollment_id}`}>
                              {busy[r.enrollment_id] === "remind" ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Remind
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setEditing(r)}
                          disabled={!!busy[r.enrollment_id]}
                          className="border border-purple text-purple hover:bg-purple/5 px-3 py-1.5 rounded-sm text-xs inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`edit-enrollment-${r.enrollment_id}`}
                          title="Edit parent info, email, students"
                        >
                          <Edit size={12} /> Edit
                        </button>
                        <button
                          onClick={() => resendEnrollmentEmail(r.enrollment_id)}
                          disabled={!!busy[r.enrollment_id]}
                          className="border border-purple text-purple hover:bg-white px-3 py-1.5 rounded-sm text-xs inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`resend-enroll-${r.enrollment_id}`}
                          title="Resend the initial enrollment-received email (works whether or not the family has paid)"
                        >
                          {busy[r.enrollment_id] === "resendmail" ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Resend enrollment email
                        </button>
                        {isPaid && (
                          <button onClick={() => resendDocs(r.enrollment_id)} disabled={!!busy[r.enrollment_id]} className="border border-purple text-purple hover:bg-white px-3 py-1.5 rounded-sm text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid={`resend-${r.enrollment_id}`}>
                            {busy[r.enrollment_id] === "resend" ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Resend docs
                          </button>
                        )}
                        {r.archived_at ? (
                          <>
                            <button
                              onClick={() => restoreEnrollment(r.enrollment_id)}
                              disabled={!!busy[r.enrollment_id]}
                              className="border border-emerald-400 text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded-sm text-xs inline-flex items-center gap-1 disabled:opacity-50"
                              data-testid={`restore-enrollment-${r.enrollment_id}`}
                              title="Restore this enrollment from the archive"
                            >
                              {busy[r.enrollment_id] === "restore" ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Restore
                            </button>
                            <button
                              onClick={() => permanentDelete(r.enrollment_id, `${r.parent_first_name} ${r.parent_last_name}`)}
                              disabled={!!busy[r.enrollment_id]}
                              className="border border-red-400 text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-sm text-xs inline-flex items-center gap-1 disabled:opacity-50"
                              data-testid={`purge-enrollment-${r.enrollment_id}`}
                              title="Permanently delete (irreversible)"
                            >
                              {busy[r.enrollment_id] === "purge" ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete forever
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => removeEnrollment(r.enrollment_id, `${r.parent_first_name} ${r.parent_last_name}`)}
                            disabled={!!busy[r.enrollment_id]}
                            className="border border-red-300 text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-sm text-xs inline-flex items-center gap-1 disabled:opacity-50"
                            data-testid={`archive-enrollment-${r.enrollment_id}`}
                            title="Archive this enrollment (recoverable)"
                          >
                            {busy[r.enrollment_id] === "delete" ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Archive
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExp && (
                      <tr className="bg-grey-light/40 border-t border-grey">
                        <td colSpan={8} className="p-4">
                          <div className="grid md:grid-cols-2 gap-4 text-xs">
                            <div>
                              <div className="font-bold text-purple-dark uppercase tracking-wider mb-2">Contact</div>
                              <div>{r.phone}</div>
                              <div className="text-ink/70">{r.address_line1}{r.address_line2 ? `, ${r.address_line2}` : ""}, {r.city}, {r.state} {r.zip_code}</div>
                            </div>
                            <div>
                              <div className="font-bold text-purple-dark uppercase tracking-wider mb-2">Students</div>
                              <ul className="space-y-1">
                                {(r.students || []).map((s, i) => (
                                  <li key={i} className="flex items-center justify-between">
                                    <span>{s.full_name} · {s.grade_level} · {s.gender} · DOB {s.date_of_birth}</span>
                                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/enrollments/${r.enrollment_id}/documents/enrollment?token=${token}&student_index=${i}`} target="_blank" rel="noreferrer" className="text-purple hover:text-purple-dark underline">
                                      Download form
                                    </a>
                                  </li>
                                ))}
                              </ul>
                              <div className="mt-3 space-x-3">
                                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/enrollments/${r.enrollment_id}/documents/welcome?token=${token}`} target="_blank" rel="noreferrer" className="text-purple hover:text-purple-dark underline">Welcome Letter PDF</a>
                                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/enrollments/${r.enrollment_id}/documents/boe?token=${token}`} target="_blank" rel="noreferrer" className="text-purple hover:text-purple-dark underline">BOE Instructions PDF</a>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-10 text-center text-ink/50">No enrollments match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------ Add Family Modal (admin-side create) ------------ */
function AddFamilyModal({ token, onClose, onCreated }) {
  const [form, setForm] = useState({
    parent_first_name: "",
    parent_last_name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "AL",
    zip_code: "",
    password: "",
    payment_method: "cash_check_now",
    mark_paid_immediately: false,
    send_welcome_email: true,
  });
  const [students, setStudents] = useState([
    { full_name: "", date_of_birth: "", grade_level: "Kindergarten", gender: "Male", status: "Active" },
  ]);
  const [saving, setSaving] = useState(false);

  const up = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setStudent = (i, k, v) =>
    setStudents((p) => p.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const addStudent = () =>
    setStudents((p) => [...p, { full_name: "", date_of_birth: "", grade_level: "Kindergarten", gender: "Male", status: "Active" }]);
  const removeStudent = (i) => setStudents((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_first_name.trim() || !form.parent_last_name.trim() || !form.email.trim()) {
      toast.error("Parent name and email are required."); return;
    }
    if (students.some((s) => !s.full_name.trim() || !s.date_of_birth.trim())) {
      toast.error("Every student needs a name and date of birth."); return;
    }
    setSaving(true);
    try {
      const res = await api.post("/admin/enrollments", { ...form, students }, { params: { token } });
      toast.success("Family added.");
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add family failed.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="add-family-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-3xl border border-grey shadow-2xl">
        <div className="bg-purple-dark text-white px-6 py-4 flex items-center justify-between">
          <div>
            <div className="h-serif text-xl">Add family from admin</div>
            <div className="text-xs text-white/70 mt-0.5">Skips the public agreement flow. Great for phone / in-person enrollments.</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none" aria-label="Close">×</button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-3">Parent / Guardian</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <TxtField label="First name*" value={form.parent_first_name} onChange={(v) => up("parent_first_name", v)} testid="af-first" />
              <TxtField label="Last name*" value={form.parent_last_name} onChange={(v) => up("parent_last_name", v)} testid="af-last" />
              <TxtField label="Email*" type="email" value={form.email} onChange={(v) => up("email", v)} testid="af-email" />
              <TxtField label="Phone" value={form.phone} onChange={(v) => up("phone", v)} testid="af-phone" />
              <TxtField label="Street address" value={form.address_line1} onChange={(v) => up("address_line1", v)} testid="af-addr1" />
              <TxtField label="Apt / suite (optional)" value={form.address_line2} onChange={(v) => up("address_line2", v)} testid="af-addr2" />
              <TxtField label="City" value={form.city} onChange={(v) => up("city", v)} testid="af-city" />
              <div className="grid grid-cols-2 gap-3">
                <TxtField label="State" value={form.state} onChange={(v) => up("state", v)} testid="af-state" />
                <TxtField label="ZIP" value={form.zip_code} onChange={(v) => up("zip_code", v)} testid="af-zip" />
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold">Students</div>
              <button type="button" onClick={addStudent} className="text-xs font-bold text-purple hover:text-purple-dark border border-purple px-3 py-1.5 rounded-sm" data-testid="af-add-student">
                + Add student
              </button>
            </div>
            <div className="space-y-3">
              {students.map((s, i) => (
                <div key={i} className="border border-grey p-3 bg-grey-light/40" data-testid={`af-student-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] tracking-widest uppercase text-purple-light font-bold">Student {i + 1}</div>
                    {students.length > 1 && (
                      <button type="button" onClick={() => removeStudent(i)} className="text-xs text-red-600 hover:text-red-800 font-bold" data-testid={`af-remove-student-${i}`}>Remove</button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <TxtField label="Full name*" value={s.full_name} onChange={(v) => setStudent(i, "full_name", v)} testid={`af-s${i}-name`} />
                    <TxtField label="Date of birth*" type="date" value={s.date_of_birth} onChange={(v) => setStudent(i, "date_of_birth", v)} testid={`af-s${i}-dob`} />
                    <div>
                      <Label className="text-[10px] tracking-widest uppercase text-purple-dark font-bold mb-1.5 block">Grade</Label>
                      <select value={s.grade_level} onChange={(e) => setStudent(i, "grade_level", e.target.value)} className="w-full border border-grey bg-white p-2 text-sm rounded-sm" data-testid={`af-s${i}-grade`}>
                        {["Kindergarten","1st Grade","2nd Grade","3rd Grade","4th Grade","5th Grade","6th Grade","7th Grade","8th Grade","9th Grade","10th Grade","11th Grade","12th Grade"].map((g) => (
                          <option key={g}>{g}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-[10px] tracking-widest uppercase text-purple-dark font-bold mb-1.5 block">Gender</Label>
                      <select value={s.gender} onChange={(e) => setStudent(i, "gender", e.target.value)} className="w-full border border-grey bg-white p-2 text-sm rounded-sm" data-testid={`af-s${i}-gender`}>
                        <option>Male</option>
                        <option>Female</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-3">Payment & Portal</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] tracking-widest uppercase text-purple-dark font-bold mb-1.5 block">Payment method</Label>
                <select value={form.payment_method} onChange={(e) => up("payment_method", e.target.value)} className="w-full border border-grey bg-white p-2 text-sm rounded-sm" data-testid="af-payment">
                  <option value="cash_check_now">Cash / check (at submission)</option>
                  <option value="payment_plan_end_of_year">Payment plan / end of year</option>
                </select>
              </div>
              <TxtField label="Portal password (blank = auto-generate)" value={form.password} onChange={(v) => up("password", v)} testid="af-pwd" hint="Any password you set here becomes the parent's portal password. Leave blank to auto-generate one that we'll show you after saving." />
            </div>
            <div className="mt-4 space-y-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="af-mark-paid-label">
                <input type="checkbox" checked={form.mark_paid_immediately} onChange={(e) => up("mark_paid_immediately", e.target.checked)} data-testid="af-mark-paid" />
                <span><b>Mark paid immediately</b> and email the enrollment PDFs (Welcome Letter, BOE Instructions, Enrollment Forms)</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="af-welcome-label">
                <input type="checkbox" checked={form.send_welcome_email} onChange={(e) => up("send_welcome_email", e.target.checked)} disabled={form.mark_paid_immediately} data-testid="af-welcome" />
                <span className={form.mark_paid_immediately ? "text-ink/40" : ""}>Send a welcome email with portal credentials (if not marking paid now)</span>
              </label>
            </div>
          </div>
        </div>

        <div className="bg-grey-light border-t border-grey px-6 py-4 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} className="border border-grey text-ink hover:bg-white px-5 py-2 rounded-sm text-sm font-bold" data-testid="af-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="bg-purple text-white hover:bg-purple-dark px-6 py-2 rounded-sm text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50" data-testid="af-submit">
            {saving && <Loader2 size={14} className="animate-spin" />} Add family
          </button>
        </div>
      </form>
    </div>
  );
}

function TxtField({ label, value, onChange, type = "text", testid, hint }) {
  return (
    <div>
      <Label className="text-[10px] tracking-widest uppercase text-purple-dark font-bold mb-1.5 block">{label}</Label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="border-grey bg-white" data-testid={testid} />
      {hint && <div className="text-[11px] text-ink/60 mt-1 leading-relaxed">{hint}</div>}
    </div>
  );
}


/* ------------ Edit Enrollment Modal ------------ */
function EditEnrollmentModal({ token, enrollment, onClose, onSaved }) {
  const [form, setForm] = useState({
    parent_first_name: enrollment.parent_first_name || "",
    parent_last_name: enrollment.parent_last_name || "",
    email: enrollment.email || "",
    phone: enrollment.phone || "",
    address_line1: enrollment.address_line1 || "",
    address_line2: enrollment.address_line2 || "",
    city: enrollment.city || "",
    state: enrollment.state || "AL",
    zip_code: enrollment.zip_code || "",
    payment_method: enrollment.payment_method || "cash_check_now",
  });
  const [students, setStudents] = useState(
    (enrollment.students || []).map((s) => ({
      full_name: s.full_name || "",
      date_of_birth: s.date_of_birth || "",
      grade_level: s.grade_level || "Kindergarten",
      gender: s.gender || "Male",
      status: s.status || "Active",
    })),
  );
  const [saving, setSaving] = useState(false);

  const up = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setStudent = (i, k, v) => setStudents((p) => p.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const addStudent = () => setStudents((p) => [...p, { full_name: "", date_of_birth: "", grade_level: "Kindergarten", gender: "Male", status: "Active" }]);
  const removeStudent = (i) => setStudents((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_first_name.trim() || !form.parent_last_name.trim() || !form.email.trim()) {
      toast.error("Parent name and email are required."); return;
    }
    if (students.some((s) => !s.full_name.trim() || !s.date_of_birth.trim())) {
      toast.error("Every student needs a name and date of birth."); return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/enrollments/${enrollment.enrollment_id}`, { ...form, students }, { params: { token } });
      toast.success("Enrollment updated.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="edit-enrollment-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-3xl border border-grey shadow-2xl">
        <div className="bg-purple-dark text-white px-6 py-4 flex items-center justify-between">
          <div>
            <div className="h-serif text-xl">Edit enrollment · {enrollment.enrollment_id}</div>
            <div className="text-xs text-white/70 mt-0.5">Update parent info, email, or student list. Amount due auto-recalculates if you add or remove students.</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none" aria-label="Close">×</button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-3">Parent / Guardian</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <TxtField label="First name*" value={form.parent_first_name} onChange={(v) => up("parent_first_name", v)} testid="edit-first" />
              <TxtField label="Last name*" value={form.parent_last_name} onChange={(v) => up("parent_last_name", v)} testid="edit-last" />
              <TxtField label="Email*" type="email" value={form.email} onChange={(v) => up("email", v)} testid="edit-email" hint="Every mailto and enrollment PDF will use this address." />
              <TxtField label="Phone" value={form.phone} onChange={(v) => up("phone", v)} testid="edit-phone" />
              <TxtField label="Street address" value={form.address_line1} onChange={(v) => up("address_line1", v)} testid="edit-addr1" />
              <TxtField label="Apt / suite (optional)" value={form.address_line2} onChange={(v) => up("address_line2", v)} testid="edit-addr2" />
              <TxtField label="City" value={form.city} onChange={(v) => up("city", v)} testid="edit-city" />
              <div className="grid grid-cols-2 gap-3">
                <TxtField label="State" value={form.state} onChange={(v) => up("state", v)} testid="edit-state" />
                <TxtField label="ZIP" value={form.zip_code} onChange={(v) => up("zip_code", v)} testid="edit-zip" />
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold">Students</div>
              <button type="button" onClick={addStudent} className="text-xs font-bold text-purple hover:text-purple-dark border border-purple px-3 py-1.5 rounded-sm" data-testid="edit-add-student">+ Add student</button>
            </div>
            <div className="space-y-3">
              {students.map((s, i) => (
                <div key={i} className="border border-grey p-3 bg-grey-light/40" data-testid={`edit-student-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] tracking-widest uppercase text-purple-light font-bold">Student {i + 1}</div>
                    {students.length > 1 && (
                      <button type="button" onClick={() => removeStudent(i)} className="text-xs text-red-600 hover:text-red-800 font-bold" data-testid={`edit-remove-student-${i}`}>Remove</button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <TxtField label="Full name*" value={s.full_name} onChange={(v) => setStudent(i, "full_name", v)} testid={`edit-s${i}-name`} />
                    <TxtField label="Date of birth*" type="date" value={s.date_of_birth} onChange={(v) => setStudent(i, "date_of_birth", v)} testid={`edit-s${i}-dob`} />
                    <div>
                      <Label className="text-[10px] tracking-widest uppercase text-purple-dark font-bold mb-1.5 block">Grade</Label>
                      <select value={s.grade_level} onChange={(e) => setStudent(i, "grade_level", e.target.value)} className="w-full border border-grey bg-white p-2 text-sm rounded-sm" data-testid={`edit-s${i}-grade`}>
                        {["Kindergarten","1st Grade","2nd Grade","3rd Grade","4th Grade","5th Grade","6th Grade","7th Grade","8th Grade","9th Grade","10th Grade","11th Grade","12th Grade"].map((g) => (<option key={g}>{g}</option>))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-[10px] tracking-widest uppercase text-purple-dark font-bold mb-1.5 block">Status</Label>
                      <select value={s.status} onChange={(e) => setStudent(i, "status", e.target.value)} className="w-full border border-grey bg-white p-2 text-sm rounded-sm" data-testid={`edit-s${i}-status`}>
                        <option>Active</option>
                        <option>Withdrawn</option>
                        <option>Dismissed</option>
                        <option>Graduated</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase text-purple-dark font-bold mb-1.5 block">Payment method</Label>
            <select value={form.payment_method} onChange={(e) => up("payment_method", e.target.value)} className="w-full sm:w-1/2 border border-grey bg-white p-2 text-sm rounded-sm" data-testid="edit-payment">
              <option value="cash_check_now">Cash / check (at submission)</option>
              <option value="payment_plan_end_of_year">Payment plan / end of year</option>
            </select>
          </div>
        </div>
        <div className="bg-grey-light border-t border-grey px-6 py-4 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} className="border border-grey text-ink hover:bg-white px-5 py-2 rounded-sm text-sm font-bold" data-testid="edit-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="bg-purple text-white hover:bg-purple-dark px-6 py-2 rounded-sm text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50" data-testid="edit-submit">
            {saving && <Loader2 size={14} className="animate-spin" />} Save changes
          </button>
        </div>
      </form>
    </div>
  );
}

function AddFamilyResultModal({ result, onClose }) {
  const copyCreds = () => {
    const lines = [
      `Enrollment ID: ${result.enrollment_id}`,
      result.generated_password ? `Portal password: ${result.generated_password}` : null,
      "Sign in at: https://zcak12.com/portal/login",
    ].filter(Boolean).join("\n");
    navigator.clipboard.writeText(lines).then(() => toast.success("Credentials copied."));
  };
  return (
    <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center px-4" data-testid="add-family-result-modal">
      <div className="bg-white w-full max-w-md border border-grey shadow-2xl">
        <div className="bg-emerald-700 text-white px-6 py-4">
          <div className="h-serif text-xl">Family added ✓</div>
          <div className="text-xs text-white/80 mt-0.5">
            {result.payment_status === "paid" ? "Marked paid and documents emailed." : "Unpaid — documents unlock once you mark paid."}
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-grey-light border border-grey p-4 text-sm font-mono">
            <div><b className="not-italic">ID:</b> {result.enrollment_id}</div>
            {result.generated_password && (
              <div className="mt-1"><b className="not-italic">Password:</b> {result.generated_password}</div>
            )}
            <div className="mt-2 text-[11px] text-ink/60">zcak12.com/portal/login</div>
          </div>
          {result.generated_password && (
            <div className="text-xs text-ink/70 leading-relaxed bg-gold/15 border-l-4 border-gold p-3">
              <b>Share these with the parent</b> — this is the only time the plain password is shown. They can reset it any time from the portal login page.
            </div>
          )}
          <div className="text-xs text-ink/60">Students: {result.student_count} · Amount due: ${result.amount_due?.toFixed?.(2) ?? result.amount_due}</div>
          <div className="flex gap-2 pt-2">
            <button onClick={copyCreds} className="flex-1 border border-purple text-purple hover:bg-purple/5 px-4 py-2 rounded-sm text-sm font-bold" data-testid="af-copy-creds">Copy credentials</button>
            <button onClick={onClose} className="flex-1 bg-purple text-white hover:bg-purple-dark px-4 py-2 rounded-sm text-sm font-bold" data-testid="af-result-close">Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}



/* ------------ Pricing Tab ------------ */
function PricingTab({ token }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => toast.error("Failed to load settings."));
  }, []);
  if (!s) return <div className="p-8"><Loader2 className="animate-spin text-purple" /></div>;
  const upd = (k, v) => setS({ ...s, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s, { params: { token } });
      toast.success("Pricing saved. Live on the site.");
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };
  const numField = (k, label) => (
    <div>
      <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">{label}</Label>
      <Input type="number" step="0.01" value={s[k] ?? 0} onChange={(e) => upd(k, parseFloat(e.target.value) || 0)} className="border-grey bg-white" data-testid={`pricing-${k}`} />
    </div>
  );
  return (
    <div className="p-8 max-w-4xl">
      <Header title="Pricing" subtitle="Every price on this page updates live across the website and future enrollments." />
      <Card title="Enrollment Tuition">
        <div className="grid sm:grid-cols-2 gap-5">
          {numField("enrollment_fee_per_child", "Fee per child (per year)")}
          {numField("transcript_fee", "Transcript fee")}
          {numField("diploma_fee", "Diploma fee")}
          {numField("report_card_fee", "Report card fee")}
          {numField("verification_letter_fee", "Verification letter fee")}
        </div>
      </Card>
      <Card title="Re-enrollment tiers (multi-year discount)">
        <div className="grid sm:grid-cols-3 gap-5">
          {numField("reenroll_1yr", "1 year")}
          {numField("reenroll_2yr", "2 years")}
          {numField("reenroll_3yr", "3 years")}
          {numField("reenroll_4yr", "4 years")}
          {numField("reenroll_5yr", "5 years")}
        </div>
      </Card>
      <Card title="Feature toggles">
        <div className="space-y-4">
          <Toggle label="Enrollment open (public /enroll)" checked={s.enrollment_open !== false} onChange={(v) => upd("enrollment_open", v)} testid="toggle-enrollment-open" />
          <Toggle label="Re-enrollment" checked={s.feature_reenrollment} onChange={(v) => upd("feature_reenrollment", v)} testid="toggle-reenrollment" />
          <Toggle label="Transcripts (parent portal)" checked={s.feature_transcripts} onChange={(v) => upd("feature_transcripts", v)} testid="toggle-transcripts" />
          <Toggle label="Student management (parent portal)" checked={s.feature_student_management} onChange={(v) => upd("feature_student_management", v)} testid="toggle-students" />
        </div>
      </Card>
      <button onClick={save} disabled={saving} className="mt-4 bg-purple text-white hover:bg-purple-dark font-bold px-8 py-3 rounded-sm text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="save-pricing-btn">
        {saving && <Loader2 size={14} className="animate-spin" />}
        {saving ? "Saving..." : "Save changes"}
      </button>
    </div>
  );
}

/* ------------ Payment Instructions Tab ------------ */
function PaymentInstructionsTab({ token }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
  }, []);
  if (!s) return <div className="p-8"><Loader2 className="animate-spin text-purple" /></div>;
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        payment_instructions_cash: s.payment_instructions_cash,
        payment_instructions_plan: s.payment_instructions_plan,
        partner_ministry_note: s.partner_ministry_note,
      }, { params: { token } });
      toast.success("Instructions saved. Live on the site.");
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };
  return (
    <div className="p-8 max-w-3xl">
      <Header title="Payment Instructions" subtitle="Shown to families on the enrollment confirmation page and in emails." />
      <Card title="Cash / Check (paid at submission)">
        <Textarea rows={5} value={s.payment_instructions_cash} onChange={(e) => setS({ ...s, payment_instructions_cash: e.target.value })} className="border-grey bg-white" data-testid="instr-cash" />
      </Card>
      <Card title="Payment plan / pay at end of year">
        <Textarea rows={5} value={s.payment_instructions_plan} onChange={(e) => setS({ ...s, payment_instructions_plan: e.target.value })} className="border-grey bg-white" data-testid="instr-plan" />
      </Card>
      <Card title="Partner ministry note (homepage tuition section)">
        <Textarea rows={3} value={s.partner_ministry_note} onChange={(e) => setS({ ...s, partner_ministry_note: e.target.value })} className="border-grey bg-white" data-testid="instr-partner" />
      </Card>
      <button onClick={save} disabled={saving} className="mt-4 bg-purple text-white hover:bg-purple-dark font-bold px-8 py-3 rounded-sm text-sm disabled:opacity-60 inline-flex items-center gap-2" data-testid="save-instructions-btn">
        {saving && <Loader2 size={14} className="animate-spin" />}
        {saving ? "Saving..." : "Save changes"}
      </button>
    </div>
  );
}

/* ------------ Site Content Tab ------------ */
function SCField({ label, keyName, hint, multiline = false, rows = 3, value, onChange }) {
  return (
    <div>
      <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">{label}</Label>
      {multiline ? (
        <textarea rows={rows} value={value || ""} onChange={(e) => onChange(keyName, e.target.value)} className="w-full border border-grey bg-white p-3 text-sm resize-y" data-testid={`sc-${keyName}`} />
      ) : (
        <Input value={value || ""} onChange={(e) => onChange(keyName, e.target.value)} className="border-grey bg-white" data-testid={`sc-${keyName}`} />
      )}
      {hint && <p className="text-xs text-ink/55 mt-1.5 leading-relaxed">{hint}</p>}
    </div>
  );
}

function SiteContentTab({ token }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/settings", { params: { token } })
      .then((r) => {
        const data = r.data || {};
        if (!Array.isArray(data.faq_items)) data.faq_items = [];
        setS(data);
      })
      .catch(() => toast.error("Failed to load site content."));
  }, [token]);
  const upd = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const setFaqField = (i, k, v) => setS((prev) => {
    const items = [...(prev.faq_items || [])];
    items[i] = { ...items[i], [k]: v };
    return { ...prev, faq_items: items };
  });
  const addFaq = () => setS((prev) => ({ ...prev, faq_items: [...(prev.faq_items || []), { q: "", a: "" }] }));
  const removeFaq = (i) => setS((prev) => ({ ...prev, faq_items: (prev.faq_items || []).filter((_, idx) => idx !== i) }));
  const moveFaq = (i, dir) => setS((prev) => {
    const items = [...(prev.faq_items || [])];
    const j = i + dir;
    if (j < 0 || j >= items.length) return prev;
    [items[i], items[j]] = [items[j], items[i]];
    return { ...prev, faq_items: items };
  });

  const save = async () => {
    setSaving(true);
    try {
      const cleanedFaq = (s.faq_items || [])
        .map((it) => ({ q: (it.q || "").trim(), a: (it.a || "").trim() }))
        .filter((it) => it.q || it.a);
      await api.put("/admin/settings", {
        // Footer
        footer_description: s.footer_description || "",
        footer_est_line: s.footer_est_line || "",
        footer_verse: s.footer_verse || "",
        footer_verse_ref: s.footer_verse_ref || "",
        // Contact
        contact_email: s.contact_email || "",
        contact_phone: s.contact_phone || "",
        contact_address_line1: s.contact_address_line1 || "",
        contact_address_line2: s.contact_address_line2 || "",
        // Header
        header_tagline: s.header_tagline || "",
        // Hero
        hero_pill_text: s.hero_pill_text || "",
        hero_headline_html: s.hero_headline_html || "",
        hero_subheadline: s.hero_subheadline || "",
        // Tuition
        tuition_headline: s.tuition_headline || "",
        tuition_subheadline: s.tuition_subheadline || "",
        tuition_option1_label: s.tuition_option1_label || "",
        tuition_option1_body: s.tuition_option1_body || "",
        tuition_option2_label: s.tuition_option2_label || "",
        tuition_option2_body: s.tuition_option2_body || "",
        // Coming Soon
        coming_soon_pill_text: s.coming_soon_pill_text || "",
        coming_soon_headline_html: s.coming_soon_headline_html || "",
        coming_soon_body: s.coming_soon_body || "",
        coming_soon_location_line1: s.coming_soon_location_line1 || "",
        coming_soon_location_line2: s.coming_soon_location_line2 || "",
        // FAQ
        faq_items: cleanedFaq,
        // Dev credit
        dev_credit_text: s.dev_credit_text || "",
        dev_credit_url: s.dev_credit_url || "",
      }, { params: { token } });
      // Preview mode: also clear any pending session-storage draft
      sessionStorage.removeItem("zca_site_preview");
      toast.success("Site content saved. Live immediately.");
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const openPreview = () => {
    const cleanedFaq = (s.faq_items || [])
      .map((it) => ({ q: (it.q || "").trim(), a: (it.a || "").trim() }))
      .filter((it) => it.q || it.a);
    const draft = { ...s, faq_items: cleanedFaq };
    sessionStorage.setItem("zca_site_preview", JSON.stringify(draft));
    window.open("/?preview=1", "_blank");
    toast.success("Preview opened in a new tab.");
  };
  if (!s) return <div className="p-8"><Loader2 className="animate-spin text-purple" size={18} /></div>;

  return (
    <div className="p-8 max-w-4xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="h-serif text-3xl text-purple-dark tracking-tight mb-1">Site Content</h1>
          <p className="text-sm text-ink/70">Edit text shown on the public site. Changes go live immediately &mdash; no redeploy needed.</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={openPreview}
            className="text-xs font-bold text-purple hover:text-purple-dark inline-flex items-center gap-1 border border-purple px-3 py-1.5 rounded-sm"
            data-testid="preview-site-content-btn"
          >
            <ExternalLink size={12} /> Preview changes
          </button>
          <a href="/" target="_blank" rel="noreferrer" className="text-xs font-bold text-purple hover:text-purple-dark inline-flex items-center gap-1 border border-purple px-3 py-1.5 rounded-sm">
            <ExternalLink size={12} /> View live
          </a>
        </div>
      </div>

      <div className="bg-gold/15 border border-gold/50 p-4 mb-6 text-xs text-ink/80 leading-relaxed">
        <b className="text-purple-dark">Tip:</b> Click <b>Preview changes</b> to open the public site with your unsaved edits applied in a new tab &mdash; nothing goes live until you press <b>Save site content</b>.
      </div>

      {/* Header */}
      <div className="bg-white border border-grey p-6 mb-4">
        <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-4">Header &mdash; tagline under logo</div>
        <SCField label="Tagline" keyName="header_tagline" value={s.header_tagline} onChange={upd} hint="Small uppercase gold text under the school name. Example: Independent Christian School · Berry, AL" />
      </div>

      {/* Homepage Hero */}
      <div className="bg-white border border-grey p-6 mb-4">
        <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-4">Homepage Hero</div>
        <div className="space-y-4">
          <SCField label="Top pill text" keyName="hero_pill_text" value={s.hero_pill_text} onChange={upd} hint='Small badge above the hero title. Example: "Independent Christian Community School · Est. 2026"' />
          <SCField label="Headline (HTML allowed)" keyName="hero_headline_html" multiline rows={3} value={s.hero_headline_html} onChange={upd} hint='HTML supported. Use <span class="text-gold-light italic">…</span> to italicize/gold-highlight a word.' />
          <SCField label="Subheadline paragraph" keyName="hero_subheadline" multiline rows={4} value={s.hero_subheadline} onChange={upd} hint="One or two sentences directly under the headline." />
        </div>
      </div>

      {/* Tuition */}
      <div className="bg-white border border-grey p-6 mb-4">
        <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-4">Tuition Section</div>
        <div className="space-y-4">
          <SCField label="Headline" keyName="tuition_headline" value={s.tuition_headline} onChange={upd} />
          <SCField label="Subheadline" keyName="tuition_subheadline" multiline rows={2} value={s.tuition_subheadline} onChange={upd} />
          <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-grey">
            <SCField label="Option 1 · title" keyName="tuition_option1_label" value={s.tuition_option1_label} onChange={upd} />
            <SCField label="Option 2 · title" keyName="tuition_option2_label" value={s.tuition_option2_label} onChange={upd} />
            <SCField label="Option 1 · body" keyName="tuition_option1_body" multiline rows={3} value={s.tuition_option1_body} onChange={upd} />
            <SCField label="Option 2 · body" keyName="tuition_option2_body" multiline rows={3} value={s.tuition_option2_body} onChange={upd} />
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="bg-white border border-grey p-6 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold">Frequently Asked Questions</div>
          <button
            type="button"
            onClick={addFaq}
            className="text-xs font-bold text-purple hover:text-purple-dark inline-flex items-center gap-1 border border-purple px-3 py-1.5 rounded-sm"
            data-testid="add-faq-btn"
          >
            + Add question
          </button>
        </div>
        {(s.faq_items || []).length === 0 && (
          <div className="text-xs text-ink/60 italic mb-3">No FAQ items yet. Click &ldquo;Add question&rdquo; to start.</div>
        )}
        <div className="space-y-4">
          {(s.faq_items || []).map((f, i) => (
            <div key={i} className="border border-grey p-4 bg-grey-light/40" data-testid={`faq-editor-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-purple-light">Item {i + 1}</div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => moveFaq(i, -1)}
                    disabled={i === 0}
                    className="text-xs text-purple hover:text-purple-dark disabled:opacity-30"
                    data-testid={`faq-up-${i}`}
                    aria-label="Move up"
                  >↑</button>
                  <button
                    type="button"
                    onClick={() => moveFaq(i, 1)}
                    disabled={i === (s.faq_items.length - 1)}
                    className="text-xs text-purple hover:text-purple-dark disabled:opacity-30"
                    data-testid={`faq-down-${i}`}
                    aria-label="Move down"
                  >↓</button>
                  <button
                    type="button"
                    onClick={() => removeFaq(i)}
                    className="text-xs font-bold text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                    data-testid={`faq-remove-${i}`}
                  >
                    <Trash2 size={11} /> Remove
                  </button>
                </div>
              </div>
              <Label className="text-[10px] tracking-[0.15em] uppercase text-purple-dark font-bold mb-1.5 block">Question</Label>
              <Input value={f.q || ""} onChange={(e) => setFaqField(i, "q", e.target.value)} className="border-grey bg-white mb-3" data-testid={`faq-q-${i}`} />
              <Label className="text-[10px] tracking-[0.15em] uppercase text-purple-dark font-bold mb-1.5 block">Answer</Label>
              <textarea rows={4} value={f.a || ""} onChange={(e) => setFaqField(i, "a", e.target.value)} className="w-full border border-grey bg-white p-3 text-sm resize-y" data-testid={`faq-a-${i}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Coming Soon (enrollment closed) */}
      <div className="bg-white border border-grey p-6 mb-4">
        <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-4">&ldquo;Coming Soon&rdquo; page &mdash; shown when enrollment is closed</div>
        <div className="space-y-4">
          <SCField label="Top pill text" keyName="coming_soon_pill_text" value={s.coming_soon_pill_text} onChange={upd} hint='Example: "Enrollment opens soon"' />
          <SCField label="Headline (HTML allowed)" keyName="coming_soon_headline_html" multiline rows={3} value={s.coming_soon_headline_html} onChange={upd} hint='HTML supported. Use <span class="text-gold-light italic">…</span> or <br class="hidden sm:block" /> for line breaks.' />
          <SCField label="Body paragraph" keyName="coming_soon_body" multiline rows={4} value={s.coming_soon_body} onChange={upd} />
          <div className="grid sm:grid-cols-2 gap-4">
            <SCField label="Location · line 1" keyName="coming_soon_location_line1" value={s.coming_soon_location_line1} onChange={upd} />
            <SCField label="Location · line 2" keyName="coming_soon_location_line2" value={s.coming_soon_location_line2} onChange={upd} />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border border-grey p-6 mb-4">
        <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-4">Footer &mdash; About the school</div>
        <div className="space-y-4">
          <SCField label="Description paragraph" keyName="footer_description" multiline rows={4} value={s.footer_description} onChange={upd} hint="Short blurb about the school shown under the logo in the footer." />
          <SCField label="Establishment / meeting-location line" keyName="footer_est_line" value={s.footer_est_line} onChange={upd} hint="Displays as gold bold uppercase text below the description. Keep it short." />
        </div>
      </div>

      {/* Contact */}
      <div className="bg-white border border-grey p-6 mb-4">
        <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-4">Contact Information (used everywhere on the site)</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <SCField label="Contact email" keyName="contact_email" value={s.contact_email} onChange={upd} hint="Auto-updates every mailto: link across the site." />
          <SCField label="Contact phone" keyName="contact_phone" value={s.contact_phone} onChange={upd} hint="Any format &mdash; digits are auto-extracted for the tel: link." />
          <SCField label="Street address (line 1)" keyName="contact_address_line1" value={s.contact_address_line1} onChange={upd} />
          <SCField label="City, State ZIP (line 2)" keyName="contact_address_line2" value={s.contact_address_line2} onChange={upd} />
        </div>
      </div>

      {/* Verse */}
      <div className="bg-white border border-grey p-6 mb-4">
        <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-4">Bottom-strip verse</div>
        <div className="space-y-4">
          <SCField label="Verse text" keyName="footer_verse" value={s.footer_verse} onChange={upd} hint='Shown italicized on the far right of the copyright strip. Do NOT include surrounding quote marks &mdash; they are added automatically.' />
          <SCField label="Verse reference" keyName="footer_verse_ref" value={s.footer_verse_ref} onChange={upd} hint='Example: — Proverbs 22:6' />
        </div>
      </div>

      {/* Developer credit */}
      <div className="bg-white border border-grey p-6 mb-6">
        <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-4">Developer credit (bottom of footer)</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <SCField label="Credit text" keyName="dev_credit_text" value={s.dev_credit_text} onChange={upd} hint='Example: "Powered by acsvirtualchurch.com". Leave blank to hide the line.' />
          <SCField label="Credit link URL (optional)" keyName="dev_credit_url" value={s.dev_credit_url} onChange={upd} hint='Full URL including https://. Leave blank for plain text.' />
        </div>
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="bg-purple text-white hover:bg-purple-dark font-bold px-8 py-3 rounded-sm text-sm inline-flex items-center gap-2 disabled:opacity-60"
        data-testid="save-site-content-btn"
      >
        {saving && <Loader2 size={14} className="animate-spin" />}
        Save site content
      </button>
    </div>
  );
}

/* ------------ Policies Tab (Enrollment + Refund) ------------ */
function PoliciesTab({ token }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    api.get("/admin/settings", { params: { token } })
      .then((r) => setS(r.data))
      .catch(() => toast.error("Failed to load policies."));
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        refund_policy_html: s.refund_policy_html || "",
        enrollment_policy_html: s.enrollment_policy_html || "",
      }, { params: { token } });
      toast.success("Policies saved. Live on the site immediately.");
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="p-8"><Loader2 className="animate-spin text-purple" size={18} /></div>;

  return (
    <div className="p-8 max-w-5xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="h-serif text-3xl text-purple-dark tracking-tight mb-1">Policies</h1>
          <p className="text-sm text-ink/70">Edit the Enrollment Policy and Refund Policy shown on the public site. HTML tags like &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;b&gt;, &lt;i&gt;, &lt;a&gt; are all supported.</p>
        </div>
        <div className="flex gap-2">
          <a href="/enrollment-policy" target="_blank" rel="noreferrer" className="text-xs font-bold text-purple hover:text-purple-dark inline-flex items-center gap-1 border border-purple px-3 py-1.5 rounded-sm" data-testid="preview-enrollment-policy">
            <ExternalLink size={12} /> View live
          </a>
        </div>
      </div>

      <div className="bg-white border border-grey p-6 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold">Enrollment Policy</div>
          <button
            onClick={() => setPreview(preview === "enrollment" ? null : "enrollment")}
            className="text-xs font-bold text-purple hover:text-purple-dark"
            data-testid="toggle-preview-enrollment"
          >
            {preview === "enrollment" ? "Hide preview" : "Show preview"}
          </button>
        </div>
        <textarea
          value={s.enrollment_policy_html || ""}
          onChange={(e) => setS({ ...s, enrollment_policy_html: e.target.value })}
          rows={20}
          className="w-full font-mono text-xs border-grey bg-grey-light/40 p-3 border resize-y"
          data-testid="enrollment-policy-editor"
        />
        {preview === "enrollment" && (
          <div className="mt-4 pt-4 border-t border-grey policy-content bg-grey-light/30 p-5" dangerouslySetInnerHTML={{ __html: s.enrollment_policy_html || "" }} />
        )}
      </div>

      <div className="bg-white border border-grey p-6 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold">Refund Policy</div>
          <button
            onClick={() => setPreview(preview === "refund" ? null : "refund")}
            className="text-xs font-bold text-purple hover:text-purple-dark"
            data-testid="toggle-preview-refund"
          >
            {preview === "refund" ? "Hide preview" : "Show preview"}
          </button>
        </div>
        <textarea
          value={s.refund_policy_html || ""}
          onChange={(e) => setS({ ...s, refund_policy_html: e.target.value })}
          rows={16}
          className="w-full font-mono text-xs border-grey bg-grey-light/40 p-3 border resize-y"
          data-testid="refund-policy-editor"
        />
        {preview === "refund" && (
          <div className="mt-4 pt-4 border-t border-grey policy-content bg-grey-light/30 p-5" dangerouslySetInnerHTML={{ __html: s.refund_policy_html || "" }} />
        )}
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="bg-purple text-white hover:bg-purple-dark font-bold px-8 py-3 rounded-sm text-sm inline-flex items-center gap-2 disabled:opacity-60"
        data-testid="save-policies-btn"
      >
        {saving && <Loader2 size={14} className="animate-spin" />}
        Save policies
      </button>
      {s.policies_updated_at && (
        <span className="ml-4 text-xs text-ink/60">
          Last updated {new Date(s.policies_updated_at).toLocaleString()}
        </span>
      )}
    </div>
  );
}

/* ------------ Web Stats Tab ------------ */
function WebStatsTab({ token }) {
  const [range, setRange] = useState(30);
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setSummary(null); setSessions(null);
    api.get("/admin/stats/summary", { params: { token, days: range } })
      .then((r) => setSummary(r.data))
      .catch(() => toast.error("Failed to load stats summary."));
    api.get("/admin/stats/sessions", { params: { token, days: range, limit: 200 } })
      .then((r) => setSessions(r.data.sessions || []))
      .catch(() => toast.error("Failed to load sessions."));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);

  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";
  const browser = (ua) => {
    if (!ua) return "—";
    if (/Edg\//.test(ua)) return "Edge";
    if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return "Chrome";
    if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return "Safari";
    if (/Firefox\//.test(ua)) return "Firefox";
    return ua.split(" ")[0] || "—";
  };

  return (
    <div className="p-8 max-w-full">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="h-serif text-3xl text-purple-dark tracking-tight mb-1">Web Stats</h1>
          <p className="text-sm text-ink/70">Visitors, page journeys, and geography.</p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setRange(d)}
              data-testid={`stats-range-${d}`}
              className={`px-3 py-1.5 text-xs font-bold rounded-sm border ${
                range === d ? "bg-purple text-white border-purple" : "bg-white text-ink border-grey hover:border-purple"
              }`}
            >
              Last {d} days
            </button>
          ))}
          <button
            onClick={load}
            className="px-3 py-1.5 text-xs font-bold rounded-sm border border-grey bg-white hover:border-purple inline-flex items-center gap-1"
            data-testid="stats-refresh"
          >
            <RotateCcw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {summary ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="stats-summary">
          <StatCard label="Total visits" value={summary.total_visits} Icon={BarChart3} />
          <StatCard label="Unique visitors" value={summary.unique_sessions} Icon={Users} />
          <StatCard label="Visits today" value={summary.today_visits} Icon={Clock} accent="gold" />
          <StatCard label="Signed-in visits" value={summary.logged_in_visits} Icon={KeyRound} />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="border border-grey bg-grey-light animate-pulse h-24" />
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4 mb-8">
        <BreakdownCard title="Top pages" items={summary?.top_pages} keyField="path" testid="top-pages" />
        <BreakdownCard title="Top countries" items={summary?.top_countries} keyField="country" testid="top-countries" IconLead={Globe} />
      </div>

      {/* Sessions table */}
      <div>
        <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-3">
          Recent sessions {sessions ? `(${sessions.length})` : ""}
        </div>
        {sessions === null ? (
          <div className="bg-white border border-grey">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="border-t border-grey animate-pulse h-14 bg-grey-light/30" />
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <div className="border border-grey bg-white p-10 text-center text-ink/50 text-sm">
            No visits recorded in the last {range} days.
          </div>
        ) : (
          <div className="bg-white border border-grey overflow-x-auto">
            <table className="w-full text-sm" data-testid="sessions-table">
              <thead className="bg-grey-light text-[10px] tracking-widest uppercase text-purple-dark">
                <tr>
                  <th className="text-left p-3">First seen</th>
                  <th className="text-left p-3">Location</th>
                  <th className="text-left p-3">IP · Browser</th>
                  <th className="text-left p-3">Pages</th>
                  <th className="text-left p-3">Signed in</th>
                  <th className="text-right p-3">Journey</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const isOpen = expanded === s.session_id;
                  return (
                    <React.Fragment key={s.session_id}>
                      <tr className="border-t border-grey hover:bg-grey-light/40" data-testid={`session-${s.session_id}`}>
                        <td className="p-3 text-xs">{fmt(s.first_seen)}</td>
                        <td className="p-3 text-xs">
                          {s.country ? (
                            <>
                              <div className="font-bold">{s.country}</div>
                              <div className="text-ink/60">{[s.city, s.region].filter(Boolean).join(", ") || "—"}</div>
                            </>
                          ) : <span className="text-ink/40">Unknown</span>}
                        </td>
                        <td className="p-3 text-xs">
                          <div className="font-mono text-ink/80">{s.ip || "—"}</div>
                          <div className="text-ink/60">{browser(s.user_agent)}</div>
                        </td>
                        <td className="p-3 text-xs font-bold text-purple">{s.page_count}</td>
                        <td className="p-3 text-xs">
                          {s.is_logged_in ? (
                            <div>
                              <span className="text-emerald-700 font-bold">✓</span>
                              <div className="text-ink/70 text-[11px]">{s.email || "yes"}</div>
                            </div>
                          ) : <span className="text-ink/40">—</span>}
                        </td>
                        <td className="p-3 text-right">
                          <button
                            onClick={() => setExpanded(isOpen ? null : s.session_id)}
                            data-testid={`journey-toggle-${s.session_id}`}
                            className="text-xs font-bold text-purple hover:text-purple-dark inline-flex items-center gap-1"
                          >
                            {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            {isOpen ? "Hide" : "Show"}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-grey-light/50" data-testid={`journey-${s.session_id}`}>
                          <td colSpan={6} className="p-4">
                            <div className="text-[10px] tracking-widest uppercase text-purple-light font-bold mb-2">Page journey · {fmt(s.first_seen)} → {fmt(s.last_seen)}</div>
                            <ol className="space-y-1 text-xs">
                              {(s.pages || []).map((p, i) => (
                                <li key={i} className="flex items-baseline gap-3">
                                  <span className="text-ink/40 font-mono w-6">{String(i + 1).padStart(2, "0")}</span>
                                  <span className="text-ink font-mono">{p}</span>
                                </li>
                              ))}
                            </ol>
                            {s.referrer && (
                              <div className="mt-3 pt-3 border-t border-grey text-[11px] text-ink/60">
                                <b>Referrer:</b>{" "}
                                <span className="font-mono break-all">{s.referrer}</span>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, Icon, accent }) {
  const bg = accent === "gold" ? "bg-gold/10 border-gold/40" : "bg-white border-grey";
  const iconClr = accent === "gold" ? "text-gold" : "text-purple-dark";
  return (
    <div className={`border p-5 ${bg}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-purple-light">{label}</div>
        {Icon && <Icon className={iconClr} size={16} />}
      </div>
      <div className="h-serif text-3xl text-purple-dark">{value?.toLocaleString?.() ?? value}</div>
    </div>
  );
}

function BreakdownCard({ title, items, keyField, testid, IconLead }) {
  const max = Math.max(1, ...(items?.map((i) => i.count) || [1]));
  return (
    <div className="border border-grey bg-white p-5" data-testid={testid}>
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-purple-light mb-4">{title}</div>
      {!items ? (
        Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="animate-pulse h-4 bg-grey-light mb-3" />
        ))
      ) : items.length === 0 ? (
        <div className="text-xs text-ink/50 py-4">Nothing to show yet.</div>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => (
            <li key={it[keyField]} className="text-xs">
              <div className="flex items-center justify-between mb-1 gap-2">
                <span className="font-mono truncate flex items-center gap-1.5">
                  {IconLead && <IconLead size={11} className="text-purple-light shrink-0" />}
                  {it[keyField]}
                </span>
                <span className="font-bold text-purple-dark">{it.count}</span>
              </div>
              <div className="h-1 bg-grey rounded-sm overflow-hidden">
                <div className="h-full bg-purple" style={{ width: `${(it.count / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------ Admin Users Tab (super_admin only) ------------ */

/* ------------ Email Diagnostics Tab ------------ */
function EmailDiagnosticsTab({ token }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [sending, setSending] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/diagnostics/email", { params: { token } });
      setStatus(r.data);
    } catch { toast.error("Failed to load diagnostics."); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, []);

  const testSend = async () => {
    if (!testTo || !testTo.includes("@")) { toast.error("Enter a valid email address to test."); return; }
    setSending(true); setTestResult(null);
    try {
      const r = await api.post(`/admin/diagnostics/email/test-send`, null, {
        params: { token, to: testTo },
      });
      setTestResult(r.data);
      if (r.data?.ok) toast.success("Test send accepted by email provider — check the inbox in 1–2 min.");
      else toast.error("Test send failed — see the details below.");
    } catch (err) {
      setTestResult({ ok: false, error: err?.response?.data?.detail || String(err) });
      toast.error("Test send request failed.");
    } finally { setSending(false); }
  };

  const verdictColor = (v) => v?.startsWith("✅") ? "bg-emerald-50 border-emerald-300 text-emerald-900"
    : v?.startsWith("⚠") ? "bg-amber-50 border-amber-300 text-amber-900"
    : v?.startsWith("❌") ? "bg-red-50 border-red-300 text-red-900"
    : "bg-grey-light border-grey text-ink";

  return (
    <div className="p-8 max-w-3xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="h-serif text-3xl text-purple-dark tracking-tight mb-1">Email Diagnostics</h1>
          <p className="text-sm text-ink/70">If enrollment emails aren&apos;t reaching families, use this tab to see what&apos;s happening.</p>
        </div>
        <button onClick={loadStatus} disabled={loading} className="border border-purple text-purple hover:bg-purple/5 px-3 py-1.5 rounded-sm text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50" data-testid="diag-refresh">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Refresh
        </button>
      </div>

      {!status ? (
        <div className="text-ink/60 text-sm"><Loader2 size={16} className="animate-spin inline mr-2" /> Loading…</div>
      ) : (
        <>
          <div className={`border p-5 mb-6 rounded-sm ${verdictColor(status.verdict)}`} data-testid="diag-verdict">
            <div className="text-xs tracking-widest uppercase font-bold mb-2">Current config verdict</div>
            <div className="text-sm">{status.verdict}</div>
          </div>

          <div className="bg-white border border-grey p-5 mb-6">
            <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-3">Environment</div>
            <table className="w-full text-sm">
              <tbody>
                {[
                  ["RESEND API key set", status.environment.RESEND_API_KEY_set ? `Yes (…${status.environment.RESEND_API_KEY_last4})` : "❌ No"],
                  ["Sender email", status.environment.SENDER_EMAIL],
                  ["TEST_EMAIL_OVERRIDE", status.environment.TEST_EMAIL_OVERRIDE_value],
                  ["PREVIEW_MODE active", status.environment.PREVIEW_MODE_active ? "Yes ⚠" : "No ✓"],
                ].map(([k, v]) => (
                  <tr key={k} className="border-b border-grey last:border-none">
                    <td className="py-2 pr-4 text-xs uppercase tracking-widest text-purple-light font-bold w-64">{k}</td>
                    <td className="py-2 font-mono text-xs text-ink">{String(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white border border-grey p-5">
            <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-3">Force-send a test email</div>
            <p className="text-xs text-ink/70 mb-3 leading-relaxed">
              This sends a plain diagnostic email directly to the address below <b>bypassing every safety filter</b>. Use it to confirm the email provider is reachable from this environment.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input type="email" placeholder="admin@alabamachurchschool.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="border-grey bg-white flex-1" data-testid="diag-test-to" />
              <button onClick={testSend} disabled={sending} className="bg-purple text-white hover:bg-purple-dark px-5 py-2 rounded-sm text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50" data-testid="diag-test-send">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Send test
              </button>
            </div>
            {testResult && (
              <pre className="mt-4 bg-grey-light border border-grey p-3 text-[11px] font-mono overflow-auto max-h-64" data-testid="diag-test-result">
                {JSON.stringify(testResult, null, 2)}
              </pre>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function AdminUsersTab({ token, user }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", role: "admin", password: "" });
  const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState(null);

  const isSuper = user?.role === "super_admin";

  const load = () => {
    setLoading(true);
    api.get("/admin/users", { params: { token } })
      .then((r) => setRows(r.data.users || []))
      .catch((err) => {
        if (err?.response?.status === 403) toast.error("Super admin privileges required.");
        else toast.error("Failed to load admin users.");
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      await api.post("/admin/users", form, { params: { token } });
      toast.success(`Invited ${form.full_name} as ${form.role.replace("_", " ")}.`);
      setShowAdd(false);
      setForm({ email: "", full_name: "", role: "admin", password: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create admin.");
    } finally { setBusy(false); }
  };

  const remove = async (email) => {
    if (!window.confirm(`Remove ${email} from admins?`)) return;
    try {
      await api.delete(`/admin/users/${encodeURIComponent(email)}`, { params: { token } });
      toast.success("Admin removed.");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed."); }
  };

  const resetPassword = async (email) => {
    const pw = window.prompt(`New password for ${email} (min 8 chars):`);
    if (!pw) return;
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setResetting(email);
    try {
      await api.post(`/admin/users/${encodeURIComponent(email)}/reset-password`, { new_password: pw }, { params: { token } });
      toast.success(`Password reset for ${email}. Share it with them securely.`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed."); }
    finally { setResetting(null); }
  };

  if (!isSuper) {
    return (
      <div className="p-8 max-w-2xl">
        <Header title="Admin Users" subtitle="Only super admins can manage other admins." />
        <div className="border border-amber-300 bg-amber-50 p-6 text-sm text-ink/85">
          Your account has the <b>admin</b> role. Contact Matthew Brady Renfroe (super admin) to add or remove admin users.
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="h-serif text-3xl text-purple-dark tracking-tight mb-1">Admin Users</h1>
          <p className="text-sm text-ink/70">People with access to this dashboard.</p>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="bg-purple text-white hover:bg-purple-dark font-bold px-4 py-2 rounded-sm text-sm inline-flex items-center gap-2"
          data-testid="add-admin-btn"
        >
          <UserPlus size={14} /> {showAdd ? "Cancel" : "Invite admin"}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={create} className="border border-purple bg-grey-light p-6 mb-6 space-y-4" data-testid="add-admin-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">Full name</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required className="border-grey bg-white" data-testid="new-admin-name" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="border-grey bg-white" data-testid="new-admin-email" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="border-grey bg-white" data-testid="new-admin-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">Temporary password (min 8)</Label>
              <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} className="border-grey bg-white" data-testid="new-admin-password" />
            </div>
          </div>
          <p className="text-xs text-ink/60">Share this password with them securely — they should change it after their first sign-in.</p>
          <button type="submit" disabled={busy} className="bg-purple text-white hover:bg-purple-dark font-bold px-6 py-2 rounded-sm text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="submit-new-admin">
            {busy && <Loader2 size={14} className="animate-spin" />} Create admin
          </button>
        </form>
      )}

      {loading ? (
        <Loader2 className="animate-spin text-purple" size={18} />
      ) : (
        <div className="bg-white border border-grey">
          <table className="w-full text-sm" data-testid="admins-table">
            <thead className="bg-grey-light text-xs tracking-widest uppercase text-purple-dark">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Added</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.email} className="border-t border-grey" data-testid={`admin-row-${a.email}`}>
                  <td className="p-3 font-bold">{a.full_name || "—"}</td>
                  <td className="p-3">{a.email}</td>
                  <td className="p-3">
                    <span className={`text-xs font-bold px-2 py-1 rounded-sm ${a.role === "super_admin" ? "bg-gold/30 text-purple-dark" : "bg-purple/10 text-purple-dark"}`}>
                      {a.role === "super_admin" ? "SUPER ADMIN" : "ADMIN"}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-ink/60">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                  <td className="p-3 text-right space-x-2">
                    <button
                      onClick={() => resetPassword(a.email)}
                      disabled={resetting === a.email}
                      className="text-xs font-bold text-purple hover:text-purple-dark inline-flex items-center gap-1"
                      data-testid={`reset-admin-${a.email}`}
                    >
                      {resetting === a.email ? <Loader2 size={11} className="animate-spin" /> : <KeyRound size={11} />} Reset password
                    </button>
                    {a.email !== user?.email && (
                      <button
                        onClick={() => remove(a.email)}
                        className="text-xs font-bold text-red-600 hover:text-red-800 inline-flex items-center gap-1 ml-3"
                        data-testid={`delete-admin-${a.email}`}
                      >
                        <Trash2 size={11} /> Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------ Transcript & Instant Records queues ------------ */
function TranscriptQueueTab({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/transcript-requests", { params: { token } })
      .then((r) => setRows(r.data.requests || []))
      .catch(() => toast.error("Failed to load transcript requests."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const markIssued = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/transcript-requests/${id}/mark-issued`, null, { params: { token } });
      toast.success("Marked issued and parent notified.");
      load();
    } catch { toast.error("Failed to mark issued."); }
    finally { setBusy(null); }
  };

  return (
    <div className="p-8 max-w-full">
      <Header title="Transcript Requests" subtitle="Every transcript submitted from the parent portal." />
      {loading ? (
        <Loader2 className="animate-spin text-purple" size={18} />
      ) : rows.length === 0 ? (
        <div className="border border-grey bg-white p-10 text-center text-ink/50 text-sm">No transcript requests yet.</div>
      ) : (
        <div className="bg-white border border-grey">
          <table className="w-full text-sm" data-testid="transcripts-table">
            <thead className="bg-grey-light text-xs tracking-widest uppercase text-purple-dark">
              <tr>
                <th className="text-left p-3">Request ID</th>
                <th className="text-left p-3">Student</th>
                <th className="text-left p-3">Parent</th>
                <th className="text-left p-3">Deliver to</th>
                <th className="text-left p-3">Fee</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.request_id} className="border-t border-grey" data-testid={`transcript-row-${r.request_id}`}>
                  <td className="p-3 font-mono text-xs text-purple-dark">{r.request_id}</td>
                  <td className="p-3"><div className="font-bold">{r.student_name}</div><div className="text-xs text-ink/60">{r.student_grade}</div></td>
                  <td className="p-3 text-xs">{r.parent_name}<br/><span className="text-ink/60">{r.parent_email}</span></td>
                  <td className="p-3 text-xs">{r.delivery_email}<br/><span className="text-ink/60 whitespace-pre-line">{r.delivery_address}</span></td>
                  <td className="p-3 text-xs">${Number(r.amount_due || 0).toFixed(2)}</td>
                  <td className="p-3"><span className={`text-xs font-bold px-2 py-1 rounded-sm ${r.status === "issued" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{r.status?.toUpperCase()}</span></td>
                  <td className="p-3 text-right">
                    {r.status !== "issued" && (
                      <button
                        onClick={() => markIssued(r.request_id)}
                        disabled={busy === r.request_id}
                        className="bg-gold text-ink hover:bg-gold-light font-bold px-3 py-1.5 rounded-sm text-xs inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`mark-transcript-issued-${r.request_id}`}
                      >
                        {busy === r.request_id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Mark issued
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InstantRecordsQueueTab({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/instant-records", { params: { token } })
      .then((r) => setRows(r.data.requests || []))
      .catch(() => toast.error("Failed to load instant records."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const markIssued = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/instant-records/${id}/mark-issued`, null, { params: { token } });
      toast.success("Marked issued and buyer notified.");
      load();
    } catch { toast.error("Failed to mark issued."); }
    finally { setBusy(null); }
  };

  const typeLabel = (t) =>
    t === "diploma" ? "Diploma" : t === "report_card" ? "Report Card" : "Verification Letter";

  return (
    <div className="p-8 max-w-full">
      <Header title="Instant Records" subtitle="Public purchase requests (diploma / report card / verification letter)." />
      {loading ? (
        <Loader2 className="animate-spin text-purple" size={18} />
      ) : rows.length === 0 ? (
        <div className="border border-grey bg-white p-10 text-center text-ink/50 text-sm">No public record requests yet.</div>
      ) : (
        <div className="bg-white border border-grey">
          <table className="w-full text-sm" data-testid="instant-records-table">
            <thead className="bg-grey-light text-xs tracking-widest uppercase text-purple-dark">
              <tr>
                <th className="text-left p-3">Request ID</th>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Student</th>
                <th className="text-left p-3">Buyer</th>
                <th className="text-left p-3">Fee · Method</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.request_id} className="border-t border-grey" data-testid={`instant-row-${r.request_id}`}>
                  <td className="p-3 font-mono text-xs text-purple-dark">{r.request_id}</td>
                  <td className="p-3 text-xs font-bold">{typeLabel(r.record_type)}</td>
                  <td className="p-3 text-xs">
                    <div className="font-bold">{r.student_full_name}</div>
                    <div className="text-ink/60">DOB {r.student_dob} · {r.prior_grade} · {r.prior_year}</div>
                  </td>
                  <td className="p-3 text-xs">
                    <div>{r.buyer_first_name} {r.buyer_last_name}</div>
                    <div className="text-ink/60">{r.buyer_email} · {r.buyer_phone}</div>
                  </td>
                  <td className="p-3 text-xs">
                    ${Number(r.amount_due || 0).toFixed(2)}<br/>
                    <span className="text-ink/60">{r.payment_method === "cash_check_now" ? "Cash/Check" : "Plan"}</span>
                  </td>
                  <td className="p-3"><span className={`text-xs font-bold px-2 py-1 rounded-sm ${r.status === "issued" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{r.status?.toUpperCase()}</span></td>
                  <td className="p-3 text-right">
                    {r.status !== "issued" && (
                      <button
                        onClick={() => markIssued(r.request_id)}
                        disabled={busy === r.request_id}
                        className="bg-gold text-ink hover:bg-gold-light font-bold px-3 py-1.5 rounded-sm text-xs inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`mark-instant-issued-${r.request_id}`}
                      >
                        {busy === r.request_id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Mark issued
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------ shared ------------ */
function Header({ title, subtitle }) {
  return (
    <div className="mb-8">
      <h1 className="h-serif text-3xl text-purple-dark tracking-tight mb-1">{title}</h1>
      <p className="text-sm text-ink/70">{subtitle}</p>
    </div>
  );
}
function Stat({ label, value, accent }) {
  const cls = accent === "emerald" ? "text-emerald-700" : accent === "amber" ? "text-amber-700" : accent === "gold" ? "text-gold" : "text-purple-dark";
  return (
    <div className="bg-white border border-grey p-5">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-purple-light mb-2">{label}</div>
      <div className={`h-serif text-3xl ${cls}`}>{value}</div>
    </div>
  );
}
function Card({ title, children }) {
  return (
    <div className="bg-white border border-grey p-6 mb-4">
      <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-4">{title}</div>
      {children}
    </div>
  );
}
function Toggle({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-sm text-ink">{label}</div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
